import type { Catalogue } from "./index";
import { pluralFor } from "./plural";

const p = pluralFor("cs");

/**
 * The Czech catalogue — the language the application ships in.
 *
 * **Typed `Catalogue`, which is `typeof en`.** That single annotation is the completeness
 * test: a key added to `en.ts` and not translated here fails `pnpm typecheck`, a key removed
 * there fails here as an excess property, and a template function whose arguments do not match
 * fails on its signature. There is no runtime fallback to English and no "missing key" path,
 * because there cannot be a missing key.
 *
 * Register: professional, concise, and consistent — the words a Czech sales team uses about
 * their own pipeline, not a literal rendering of the English.
 *
 * Two grammatical decisions worth knowing before editing:
 *
 * - **Counted nouns go through `p()`**, never a ternary. Czech has four plural categories and
 *   `other` is the ≥5 form: *1 příležitost · 3 příležitosti · 5 příležitostí*. See
 *   `plural.ts`.
 * - **The audit timeline is nominalised.** Czech verbs agree with the actor's gender and an
 *   audit row does not record one, so "changed the name" becomes *změna názvu* and the
 *   timeline joins it to the name with a dash rather than a space. Guessing *změnil* would be
 *   wrong for half the team.
 */
export const cs: Catalogue = {
  // ─── the shell ────────────────────────────────────────────────────────────────
  "app.name": "Sales Board",
  "app.homeLink": "Sales Board — domů",
  "app.logoAlt": "INFA Partner",

  "nav.main": "Hlavní navigace",
  "nav.pipeline": "Pipeline",
  "nav.activity": "Aktivita",
  "nav.clients": "Klienti",
  "nav.managers": "Manažeři",
  "nav.signOut": "Odhlásit se",
  "nav.language": "Jazyk",

  "locale.cs": "Čeština",
  "locale.en": "English",

  "connection.reconnecting": "Obnovujeme spojení — změny ostatních se nemusí zobrazit a přesouvání karet je pozastaveno.",
  "connection.stale": "Tato tabule může být neaktuální. Obnovujeme spojení — přesouvání karet je pozastaveno.",
  "session.checking": "Kontrolujeme přihlášení…",

  // ─── role, state and status vocabulary ────────────────────────────────────────
  "role.sales_manager": "Obchodní manažer",
  "role.head_of_sales": "Vedoucí obchodu",

  // Neuter forms throughout: they describe the account, and so avoid guessing the person's
  // gender — which no row in this product records.
  "userState.invited": "Pozváno",
  "userState.active": "Aktivní",
  "userState.deactivated": "Deaktivováno",

  "status.open": "Otevřeno",
  "status.won": "Vyhráno",
  "status.lost": "Prohráno",

  // ─── words that recur everywhere ──────────────────────────────────────────────
  "common.cancel": "Zrušit",
  "common.saving": "Ukládání…",
  "common.working": "Pracuji…",
  "common.done": "Hotovo",
  "common.close": "Zavřít",
  "common.dismiss": "Skrýt",
  "common.edit": "Upravit",
  "common.saveChanges": "Uložit změny",
  "common.rename": "Přejmenovat",
  "common.deactivate": "Deaktivovat",
  "common.reactivate": "Znovu aktivovat",
  "common.all": "Vše",
  "common.none": "Žádná",
  "common.you": "(vy)",
  "common.copy": "Kopírovat",
  "common.copied": "Zkopírováno",
  "common.showLess": "Zobrazit méně",
  "common.tryAgain": "Zkusit znovu",
  "common.loading": "Načítání…",
  "date.choose": "Vyberte datum",
  "date.previousMonth": "Předchozí měsíc",
  "date.nextMonth": "Následující měsíc",

  "common.paused": "Pozastaveno, dokud se neobnoví spojení",
  "common.savingStatus": "Ukládání",

  // Viz `formatSignIn`. Instrumentál: „před 1 dnem“, „před 2 dny“, „před 5 dny“.
  "signIn.today": (time: string) => `Dnes, ${time}`,
  "signIn.yesterday": (time: string) => `Včera, ${time}`,
  "signIn.daysAgo": (n: number) =>
    `před ${n} ${p(n, { one: "dnem", few: "dny", many: "dny", other: "dny" })}`,

  // ─── the four designed states ─────────────────────────────────────────────────
  "state.noResultTitle": "Těmto filtrům nic neodpovídá",
  "state.noResultTitleScoped": (scope: string) => `Těmto filtrům nic neodpovídá — ${scope}`,
  "state.filteringBy": (filters: string) => `Filtrováno podle: ${filters}.`,
  "state.clearFilters": "Zrušit filtry",
  "state.errorTitle": "Něco se nepovedlo",
  "state.reference": "Reference",
  "state.referenceHint": "uveďte ji, pokud se obrátíte na podporu.",

  // ─── tables, toolbars, pagination ─────────────────────────────────────────────
  "table.search": "Hledat",
  "table.searchPlaceholder": "Hledat…",
  "table.clearSearch": "Vymazat hledání",
  "table.filter": "Filtr",
  "table.filterCount": (active: number) =>
    `Filtr (${active} ${p(active, { one: "aktivní", few: "aktivní", other: "aktivních" })})`,
  "table.clearAll": "Zrušit vše",
  "table.rowActions": "Akce",
  "table.removeFilter": (label: string) => `Odebrat filtr: ${label}`,

  "pagination.range": (from: string, to: string, total: string) => `${from}–${to} z ${total}`,
  "pagination.rowsPerPage": "Řádků na stránku",
  "pagination.previous": "Předchozí stránka",
  "pagination.next": "Další stránka",

  // ─── filter vocabulary ───────────────────────────────────────────────────────
  "filter.searchChip": (term: string) => `„${term}“`,
  "filter.valueChip": (label: string, value: string) => `${label}: ${value}`,
  "filter.owner": "Obchodní manažer",
  "filter.client": "Klient",
  "filter.stage": "Fáze",
  "filter.quarter": "Kvartál",
  "filter.stalledOnly": "Jen stagnující",
  // Impersonal "vybráno 3" rather than a counted noun: the noun would have to be the filter's
  // own (fáze/klienti/kvartály), and the control does not know which one it is rendering.
  "filter.selectedCount": (n: number) => `vybráno ${n}`,
  "filter.clearSelection": "Zrušit výběr",
  "filter.noOptions": "Není z čeho vybírat",
  "filter.role": "Role",
  "filter.state": "Stav",
  "filter.actor": "Osoba",
  "filter.category": "Kategorie",
  "filter.quarterOption": (quarter: number) => `Q${quarter}`,

  // ─── counted phrases ─────────────────────────────────────────────────────────
  "count.opportunities": (n: number) =>
    `${n} ${p(n, { one: "příležitost", few: "příležitosti", other: "příležitostí" })}`,
  "count.deals": (n: number) => `${n} ${p(n, { one: "obchod", few: "obchody", other: "obchodů" })}`,
  "count.days": (n: number) => `${n} ${p(n, { one: "den", few: "dny", many: "dne", other: "dní" })}`,
  "count.daysInStage": (n: number) =>
    `${n} ${p(n, { one: "den", few: "dny", many: "dne", other: "dní" })} ve fázi`,
  "count.daysShort": (n: number) => `${n} d`,
  "count.moreCards": (n: number) => `+ ${n} ${p(n, { one: "další", few: "další", other: "dalších" })}`,

  // ─── the pipeline surface ────────────────────────────────────────────────────
  "pipeline.title": "Pipeline",
  "pipeline.titleYear": (year: number) => `Pipeline ${year}`,
  "pipeline.newOpportunity": "Nová příležitost",
  "pipeline.fullscreen": "Celá obrazovka",
  "pipeline.analytics": "Analytika",
  "pipeline.leavePresentation": "Ukončit režim prezentace",
  "pipeline.leavePresentationHint": "Ukončit režim prezentace (Esc)",
  "pipeline.searchPlaceholder": "Hledat podle příležitosti nebo klienta…",
  "pipeline.yearFailed": "Rok pipeline se nepodařilo zjistit.",

  "view.label": "Zobrazení",
  "viewOptions.label": "Možnosti zobrazení",
  "view.board": "Tabule",
  "view.list": "Seznam",

  "density.label": "Velikost karet",
  "density.normal": "Karty",
  "density.condensed": "Zhuštěně",

  "year.label": "Rok pipeline",
  "year.archive": (year: number) => `${year} (archiv)`,

  // ─── the board ───────────────────────────────────────────────────────────────
  "board.region": (year: number) => `Obchodní tabule, ${year}`,
  "board.quarterHeader": (quarter: number) => `Q${quarter}`,
  "board.cell": (stage: string, quarter: number, opportunities: string, value: string) =>
    `${stage}, Q${quarter}: ${opportunities}, ${value}`,
  "board.moreCardsIn": (stage: string, quarter: number) => ` ve fázi ${stage}, Q${quarter}`,
  "board.blockedDrag": (owner: string) =>
    `Vlastníkem je ${owner} — přesunout to může jen on nebo vedoucí obchodu.`,
  "board.oneMoveAtATime": "Jeden přesun po druhém — nejdřív dokončete ten probíhající.",
  "board.moved": (name: string, target: string) => `${name} přesunuto do ${target}`,
  "board.filteredWith": (filters: string) => `Filtrováno: ${filters}`,
  "board.archiveYear": "Archivní rok — jen ke čtení",
  "board.loadFailed": "Tabuli se nepodařilo načíst.",
  "board.emptyTitle": (year: number) => `Na tabuli za rok ${year} nejsou žádné příležitosti`,
  "board.emptyDescription":
    "Příležitost potřebuje čtyři hodnoty: klienta, název, očekávaný obrat a očekávané datum uzavření. Fáze a kvartál z nich vyplynou.",
  "board.noResultScope": (year: number) => `tabuli za rok ${year}`,

  // ─── the card ────────────────────────────────────────────────────────────────
  "card.stalled": "Stagnuje",
  "card.stalledFor": (days: number) => `Bez pohybu ${days} ${p(days, { one: "den", few: "dny", many: "dne", other: "dní" })}`,
  "card.stalledPrefix": "Stagnuje, ",
  "card.stalledTooltip": (days: string) => `Stagnuje — ${days}`,
  "card.stalledAria": (days: string) => `stagnuje, ${days}`,
  "card.closedWon": "uzavřeno jako vyhrané",
  "card.closedLost": "uzavřeno jako prohrané",
  "card.ownedBy": (owner: string) => `vlastník ${owner}`,
  "card.notMovable": "tuto kartu nemůžete přesouvat",

  // ─── the opportunity list ────────────────────────────────────────────────────
  "opportunities.title": "Příležitosti",
  "opportunities.caption": "Příležitosti",
  "opportunities.loading": "Načítání příležitostí…",
  "opportunities.loadFailed": "Pipeline se nepodařilo načíst.",
  "opportunities.emptyTitle": "Zatím žádné příležitosti",
  "opportunities.emptyTitleYear": (year: number) => `V pipeline za rok ${year} nejsou žádné příležitosti`,
  "opportunities.emptyDescription":
    "Příležitost potřebuje čtyři hodnoty: klienta, název, očekávaný obrat a očekávané datum uzavření. Ostatní z nich vyplyne.",
  "opportunities.noResultScope": (year: number) => `pipeline za rok ${year}`,

  // ─── column headers, shared across screens ───────────────────────────────────
  "column.client": "Klient",
  "column.clientName": "Název klienta",
  "column.opportunity": "Příležitost",
  "column.stage": "Fáze",
  "column.expectedRevenue": "Očekávaný obrat",
  "column.expectedClose": "Očekávané uzavření",
  "column.owner": "Vlastník",
  "column.name": "Jméno",
  "column.email": "E-mail",
  "column.role": "Role",
  "column.colour": "Barva",
  "column.state": "Stav",
  "column.lastSignIn": "Poslední přihlášení",
  "column.openOpportunities": "Otevřené příležitosti",
  "column.openPipeline": "Otevřená pipeline",
  "column.order": "Pořadí",
  "column.type": "Typ",

  // ─── creating an opportunity ─────────────────────────────────────────────────
  "opportunityForm.title": "Nová příležitost",
  "opportunityForm.submit": "Vytvořit příležitost",
  "opportunityForm.client": "Klient",
  "opportunityForm.chooseClient": "Vyberte klienta…",
  "opportunityForm.name": "Příležitost",
  "opportunityForm.revenue": "Očekávaný obrat (CZK)",
  "opportunityForm.date": "Očekávané datum uzavření",
  "opportunityForm.owner": "Vlastník",
  "opportunityForm.ownerSelf": "Vy",

  // ─── the opportunity detail ──────────────────────────────────────────────────
  "opportunity.loading": "Načítání příležitosti…",
  "opportunity.notFound": "Příležitost nenalezena",
  "opportunity.loadFailed": "Tuto příležitost se nepodařilo načíst.",
  "opportunity.backToBoard": "Zpět na tabuli",
  "opportunity.reopen": "Znovu otevřít příležitost",
  "opportunity.delete": "Smazat příležitost",
  "opportunity.name": "Název příležitosti",
  "opportunity.stage": "Fáze",
  "opportunity.expectedClose": "Očekávané uzavření",
  "opportunity.expectedCloseDate": "Očekávané datum uzavření",
  "opportunity.expectedRevenue": "Očekávaný obrat",
  "opportunity.salesManager": "Obchodní manažer",
  "opportunity.actualClose": "Skutečné uzavření",
  "opportunity.lastModified": "Naposledy změněno",
  "opportunity.notes": "Poznámka",
  "opportunity.noNote": "Žádná poznámka.",
  "opportunity.notePlaceholder": "—",
  "opportunity.lostReason": "Důvod prohry",
  "opportunity.lostReasonPlaceholder": "Uveďte důvod prohry…",
  "opportunity.lostReasonTooShort": (min: number) => `Uveďte důvod, alespoň ${min} znaky`,
  "opportunity.lostReasonRequired": "Příležitost je nyní prohraná. Než budete pokračovat, uveďte důvod.",
  "opportunity.lostReasonFor": (name: string) => `Proč byla příležitost ${name} prohraná?`,
  "opportunity.lostReasonRemaining": (n: number) => `Zbývá vysvětlit: ${n}`,
  "opportunity.addNote": "+ Přidat poznámku",
  "opportunity.showMore": "Zobrazit další informace",
  "opportunity.history": "Historie",
  "opportunity.historyLoading": "Načítání historie…",
  "opportunity.historyFailed": "Historii se nepodařilo načíst.",
  "opportunity.openProfile": (name: string) => `Otevřít profil: ${name}`,
  "opportunity.openManager": "Otevřít manažera",
  "opportunity.reassignTitle": (name: string) => `Přeřadit na ${name}?`,
  "opportunity.reassignConsequence": (name: string) =>
    `Za tuto příležitost bude odpovídat ${name}; na tabuli přejde do jeho barvy a do jeho filtru. Doba ve fázi se ` +
    `nenuluje — obchod, který čeká padesát dní, čeká padesát dní i po přeřazení.`,
  "opportunity.reassignConfirm": "Přeřadit příležitost",
  "opportunity.deleteTitle": (name: string) => `Smazat ${name}?`,
  "opportunity.deleteConsequence":
    "Odejde z pipeline i ze všech součtů, ale historie zůstane. Obnovit ji může jen vedoucí obchodu.",

  // ─── the audit timeline ──────────────────────────────────────────────────────
  "audit.noHistory": "Zatím žádná historie.",
  "audit.showAll": (total: number) => `Zobrazit celou historii (${total})`,
  "audit.repeated": (times: number) => `×${times}`,
  "audit.actorSeparator": " — ",

  "audit.created": "vytvoření příležitosti",
  "audit.client_changed": "změna klienta",
  "audit.name_changed": "změna názvu",
  "audit.nip_changed": "změna očekávaného obratu",
  "audit.expected_close_date_changed": "změna očekávaného data uzavření",
  "audit.stage_changed": "změna fáze",
  "audit.closed_won": "uzavření jako vyhrané",
  "audit.closed_lost": "uzavření jako prohrané",
  "audit.reopened": "znovuotevření",
  "audit.owner_reassigned": "přeřazení na",
  "audit.notes_changed": "změna poznámky",
  "audit.lost_reason_changed": "zaznamenání důvodu prohry",
  "audit.notes_added": "přidání poznámky",
  "audit.notes_removed": "odstranění poznámky",
  "audit.deleted": "smazání",
  "audit.restored": "obnovení",
  "audit.user_invited": "pozvání uživatele",
  "audit.invitation_resent": "opětovné zaslání pozvánky",
  "audit.invitation_revoked": "zrušení pozvánky",
  "audit.credential_created": "nastavení hesla",
  "audit.password_reset": "obnovení hesla",
  "audit.role_changed": "změna role",
  "audit.user_deactivated": "deaktivace uživatele",
  "audit.user_reactivated": "znovuaktivace uživatele",
  "audit.color_changed": "změna barvy na tabuli",
  "audit.stage_created": "přidání fáze",
  "audit.stage_renamed": "přejmenování fáze",
  "audit.stage_type_changed": "změna významu fáze",
  "audit.stage_reordered": "přesun fáze",
  "audit.stage_color_changed": "změna barvy fáze",
  "audit.stage_deactivated": "deaktivace fáze",
  "audit.stage_reactivated": "znovuaktivace fáze",
  "audit.setting_changed": "změna nastavení",

  /*
   * The **feed** phrasing of each event: the same events as `audit.*` above, worded so the
   * record follows the verb — "created opportunity «Data warehouse assessment»" rather than
   * "created this opportunity".
   *
   * Two sets, deliberately. Inside one record's own history the record is a given, and
   * naming it in every row would be a stutter; a company-wide feed has the opposite problem —
   * every row is about something else, and "deleted this" names nothing at all.
   */
  "feed.created": "vytvoření příležitosti",
  "feed.client_changed": "změna klienta u",
  "feed.name_changed": "přejmenování",
  "feed.nip_changed": "změna očekávaného obratu u",
  "feed.expected_close_date_changed": "změna očekávaného data uzavření u",
  "feed.stage_changed": "změna fáze u",
  "feed.closed_won": "uzavření jako vyhrané",
  "feed.closed_lost": "uzavření jako prohrané",
  "feed.reopened": "znovuotevření",
  "feed.owner_reassigned": "přeřazení",
  "feed.notes_changed": "změna poznámky u",
  "feed.lost_reason_changed": "zaznamenání důvodu prohry u",
  "feed.notes_added": "přidání poznámky k",
  "feed.notes_removed": "odstranění poznámky u",
  "feed.deleted": "smazání příležitosti",
  "feed.restored": "obnovení příležitosti",
  "feed.user_invited": "pozvání uživatele",
  "feed.invitation_resent": "opětovné zaslání pozvánky uživateli",
  "feed.invitation_revoked": "zrušení pozvánky uživatele",
  "feed.credential_created": "nastavení hesla uživatele",
  "feed.password_reset": "obnovení hesla uživatele",
  "feed.role_changed": "změna role uživatele",
  "feed.user_deactivated": "deaktivace uživatele",
  "feed.user_reactivated": "znovuaktivace uživatele",
  "feed.color_changed": "změna barvy na tabuli uživatele",
  "feed.stage_created": "přidání fáze",
  "feed.stage_renamed": "přejmenování fáze",
  "feed.stage_type_changed": "změna významu fáze",
  "feed.stage_reordered": "přesun fáze",
  "feed.stage_color_changed": "změna barvy fáze",
  "feed.stage_deactivated": "deaktivace fáze",
  "feed.stage_reactivated": "znovuaktivace fáze",
  "feed.setting_changed": "změna nastavení",

  // ─── the company-wide activity feed ──────────────────────────────────────────
  "activity.title": "Aktivita",
  "activity.searchPlaceholder": "Hledat aktivitu",
  "activity.description": "Vše, co se stalo, od nejnovějšího.",
  "activity.loading": "Načítání aktivity…",
  "activity.loadFailed": "Přehled aktivity se nepodařilo načíst.",
  "activity.emptyTitle": "Zatím se nic nestalo",
  "activity.emptyDescription":
    "Tady je záznam každé změny v aplikaci. Jakmile kdokoli něco založí, přesune nebo upraví, objeví se to zde.",
  "activity.categoryOpportunities": "Příležitosti",
  "activity.categoryAdministration": "Správa",
  "activity.categoryConfiguration": "Konfigurace",
  "activity.deletedRecord": "(odstraněno)",

  // ─── the lifecycle dialogs ───────────────────────────────────────────────────
  "move.quarterHint": "Kvartál vyplývá z tohoto data — odvozuje ho server.",
  "move.derivedPrefix": "Toto datum spadá do",
  "move.derivedSuffix": ".",
  "move.contradictsColumn": (dropped: number, derived: number) =>
    `Kartu jste položili do Q${dropped}, ale toto datum ji řadí do Q${derived}. Rozhoduje datum — karta se objeví tam.`,
  "move.crossesYear": (to: number, from: number) =>
    `Tímto se karta přesune na tabuli za rok ${to}, takže z tabule za rok ${from} zmizí.`,

  "close.titleWon": (name: string) => `Uzavřít ${name} jako vyhrané?`,
  "close.titleLost": (name: string) => `Uzavřít ${name} jako prohrané?`,
  "close.submitWon": "Uzavřít jako vyhrané",
  "close.submitLost": "Uzavřít jako prohrané",
  "close.actualCloseDate": "Skutečné datum uzavření",

  "reopen.title": (name: string) => `Znovu otevřít ${name}?`,
  "reopen.description": (stage: string, from: number, to: number) =>
    `Do fáze ${stage} a z Q${from} do Q${to}.`,
  "reopen.consequence": (stage: string) =>
    `Vrátí se do fáze ${stage} a do provozních součtů pipeline, skutečné datum uzavření se vymaže a doba ve fázi se počítá znovu od teď.`,
  "reopen.submit": "Znovu otevřít příležitost",
  "reopen.newDate": "Nové očekávané datum uzavření",

  // ─── the inline editing kit ──────────────────────────────────────────────────
  "inline.clickToEdit": (field: string) => `Kliknutím upravíte: ${field.toLowerCase()}`,
  "inline.textareaHint": "Uloží se, když kliknete jinam. Escape změnu zahodí.",

  // ─── clients ─────────────────────────────────────────────────────────────────
  "clients.title": "Klienti",
  "clients.new": "Nový klient",
  "clients.searchPlaceholder": "Hledat klienty…",
  "clients.loading": "Načítání klientů…",
  "clients.loadFailed": "Seznam klientů se nepodařilo načíst.",
  "clients.emptyTitle": "Zatím žádní klienti",
  "clients.emptyDescription":
    "Klienti jsou registr, na který se odkazuje každá příležitost. Přidejte prvního a můžete začít zaznamenávat obchody.",
  "clients.caption": "Klienti",
  "clients.active": "Aktivní",
  "clients.inactive": "Neaktivní",

  "client.label": "Klient",
  "client.loading": "Načítání klienta…",
  "client.notFound": "Klient nenalezen",
  "client.loadFailed": "Tohoto klienta se nepodařilo načíst.",
  "client.backToClients": "Zpět na klienty",
  "client.opportunities": "Příležitosti",
  "client.opportunitiesCaption": (name: string) => `Příležitosti klienta ${name}`,
  "client.noOpportunities": "Zatím žádné příležitosti",
  "client.noOpportunitiesFor": (name: string) => `U klienta ${name} nebylo nic založeno.`,
  "client.deactivateTitle": (name: string) => `Deaktivovat ${name}?`,
  "client.reactivateTitle": (name: string) => `Znovu aktivovat ${name}?`,
  "client.deactivateConsequence": (open: string) =>
    `Přestane se nabízet při zakládání nové práce. Jeho otevřené příležitosti (${open}) to neovlivní.`,
  "client.reactivateConsequence": "Znovu se začne nabízet při zakládání nové práce.",
  "client.deactivateConfirm": "Deaktivovat klienta",
  "client.reactivateConfirm": "Znovu aktivovat klienta",

  "clientForm.createTitle": "Nový klient",
  "clientForm.renameTitle": "Přejmenovat klienta",
  "clientForm.createDescription": "Klienti jsou společní — všichni vybírají ze stejného registru.",
  "clientForm.createSubmit": "Vytvořit klienta",
  "clientForm.renameSubmit": "Uložit název",
  "clientForm.name": "Název klienta",
  "clientForm.nameRequired": "Zadejte název klienta",
  "clientForm.duplicateTitle": "Vypadá to na existujícího klienta",
  "clientForm.inactiveTag": "(neaktivní)",
  "clientForm.createAnyway": "Přesto vytvořit",

  // ─── the team-member profile ─────────────────────────────────────────────────
  "team.loading": "Načítání profilu…",
  "team.notFound": "Osoba nenalezena",
  "team.loadFailed": "Tento profil se nepodařilo načíst.",
  "team.state": "Stav",
  "team.openOpportunities": "Otevřené příležitosti",
  "team.openPipeline": "Otevřená pipeline",
  "team.pipelineLoading": "Načítání jeho pipeline…",
  "team.nothingOpen": "Nic otevřeného",
  "team.nothingOpenFor": (name: string) => `${name} teď v pipeline nevlastní žádnou otevřenou příležitost.`,
  "team.ownedCaption": (name: string) => `Otevřené příležitosti ve vlastnictví: ${name}`,
  "team.showingOf": (shown: number, total: number) =>
    `Zobrazeno ${shown} z ${total}. Vyfiltrujte pipeline podle tohoto manažera a uvidíte všechny.`,
  "team.accountHistory": "Historie účtu",
  "team.sections": "Sekce profilu",
  "team.accountHistoryNote": "Pozvánky a změny role a stavu. Vidí je jen vedoucí obchodu.",
  "team.accountHistoryLoading": "Načítání historie účtu…",
  "team.accountHistoryFailed": "Historii účtu se nepodařilo načíst.",

  // ─── user administration ─────────────────────────────────────────────────────
  "users.title": "Uživatelé",
  "users.new": "Nový uživatel",
  "users.settingsLink": "Nastavení",
  "users.searchPlaceholder": "Hledat podle jména nebo e-mailu…",
  "users.loading": "Načítání uživatelů…",
  "users.loadFailed": "Seznam uživatelů se nepodařilo načíst.",
  "users.emptyTitle": "Zatím žádní uživatelé",
  "users.emptyDescription": "Uživatelé vznikají pozvánkou. Přidejte prvního a získá přístup.",
  "users.caption": "Uživatelé",
  "users.editTitle": (name: string) => `Upravit ${name}`,
  "users.ownRoleLocked": "Vlastní roli změnit nelze.",
  "users.ownStateLocked": "Vlastní účet deaktivovat nelze.",
  "users.rowActions": (name: string) => `Akce pro ${name}`,
  "users.openOpportunitiesFor": (name: string, n: number) =>
    `${n} otevřených příležitostí manažera ${name} — otevřít nástěnku`,
  "users.invite": "Pozvat",
  "users.reinvite": "Pozvat znovu",
  "users.revokeInvitation": (name: string) => `Zrušit pozvánku — ${name}`,
  "users.invitationTitle": (name: string) => `Pozvánka pro ${name}`,
  "users.invitationConsequence": "Případný předchozí odkaz pro tohoto uživatele byl zneplatněn.",
  "users.invitationLinkFor": (name: string) => `Odkaz s pozvánkou pro ${name}`,
  "users.deactivateTitle": (name: string) => `Deaktivovat ${name}?`,
  "users.deactivateConsequence": (owned: string) =>
    `Přístup ztratí okamžitě — všechna přihlášení i případná nevyužitá pozvánka se zneplatní. Zůstane vlastníkem ` +
    `otevřených příležitostí (${owned}) a jeho historie zůstane nezměněná.`,
  "users.deactivateConfirm": "Deaktivovat uživatele",
  "users.invitedAt": (when: string) => `pozvání ${when}`,
  "users.entraOnly": "Jen Entra ID",

  "userForm.title": "Nový uživatel",
  "userForm.description": "Uživatelé vznikají pozvánkou — registrace neexistuje.",
  "userForm.submit": "Vytvořit uživatele",
  "userForm.name": "Jméno",
  "userForm.email": "E-mail",
  "userForm.role": "Role",
  "userForm.sendInvitation": "Odeslat pozvánku e-mailem hned",
  "userForm.sendInvitationHint": "Odkaz dostanete v obou případech, takže ho můžete předat sami.",
  "userForm.createdTitle": (name: string) => `${name} — vytvořeno`,
  "userForm.emailSent": "E-mail s pozvánkou je na cestě.",
  "userForm.emailNotSent": "Žádný e-mail nebyl odeslán — odkaz předejte sami.",
  "invitation.validUntil": (when: string) =>
    `Platný do ${when}. Na jedno použití — vydáním nového odkazu tento zneplatníte.`,

  // ─── the manager palette ─────────────────────────────────────────────────────
  "colour.rose": "Růžová",
  "colour.amber": "Jantarová",
  "colour.lime": "Limetková",
  "colour.emerald": "Smaragdová",
  "colour.cyan": "Tyrkysová",
  "colour.blue": "Modrá",
  "colour.violet": "Fialová",
  "colour.fuchsia": "Purpurová",
  "colour.stone": "Kamenná",
  "colour.none": "Žádná",
  "colour.forNewUser": "Barva: ",
  "colour.forUser": (name: string) => `Barva — ${name}: `,
  "colour.forStage": (name: string) => `Barva fáze ${name}: `,
  "colour.sharedWarning": "Tuto barvu má i jiný manažer",
  "colour.neutralNote": "Karty zůstávají neutrální, dokud manažer nemá barvu.",
  "settings.title": "Nastavení",
  "settings.tabsLabel": "Oddíly nastavení",
  "settings.stagesTitle": "Obchodní fáze",
  "settings.stagesDescription": "Řádky tabule, v pořadí. Uzavřené fáze vždy následují po otevřených.",
  "settings.newStage": "Nová fáze",
  "settings.stagesLoading": "Načítání fází…",
  "settings.stagesFailed": "Fáze se nepodařilo načíst.",
  "settings.stagesCaption": "Obchodní fáze",
  "settings.stageBlockedHolds": (held: string) => `Obsahuje ${held} — přesuňte je jinam a pak fázi změňte nebo zrušte.`,
  "settings.stageBlockedOnlyOpen": "Jediná otevřená fáze — tabule potřebuje od každého typu jednu.",
  "settings.stageBlockedOnlyWon": "Jediná vyhraná fáze — tabule potřebuje od každého typu jednu.",
  "settings.stageBlockedOnlyLost": "Jediná prohraná fáze — tabule potřebuje od každého typu jednu.",
  "settings.stageActive": "Aktivní",
  "settings.stageInactive": "Neaktivní",
  "settings.moveUp": (name: string) => `Posunout ${name} výš`,
  "settings.alreadyFirst": "Už je první.",
  "settings.alreadyLast": "Už je poslední.",
  "settings.stageRowActions": (name: string) => `Akce pro ${name}`,
  "settings.editStageTitle": (name: string) => `Upravit ${name}`,
  "settings.moveDown": (name: string) => `Posunout ${name} níž`,
  "settings.addStage": "Přidat fázi",
  "settings.stageName": "Název",
  "settings.stageType": "Typ",
  "settings.stageTypeOpen": "Otevřená — probíhající práce",
  "settings.stageTypeWon": "Vyhraná — uzavřený výsledek",
  "settings.stageTypeLost": "Prohraná — uzavřený výsledek",
  "settings.stageTypeHint": "Uzavřená fáze zaznamenává výsledek a vždy stojí až za otevřenými.",
  "settings.stageTypeDialogDescription":
    "Stav příležitosti vyplývá z fáze, ve které je, takže tímto měníte to, co tabule vykazuje.",
  "settings.stageMovesToOpen": "Přesune se na konec otevřených fází.",
  "settings.stageMovesToClosed": "Přesune se mezi uzavřené fáze, za všechny otevřené.",
  "settings.deactivateStageTitle": (name: string) => `Deaktivovat ${name}?`,
  "settings.deactivateStageConsequence":
    "Řádek zmizí z tabule všem. Historie zůstane a fázi lze znovu aktivovat.",
  "settings.deactivateStageConfirm": "Deaktivovat fázi",
  "settings.reactivateStageTitle": (name: string) => `Znovu aktivovat ${name}?`,
  "settings.reactivateStageConsequence": "Řádek se vrátí na tabuli všem, za ostatní fáze svého typu.",
  "settings.reactivateStageConfirm": "Znovu aktivovat fázi",
  "settings.reorderTitle": (name: string) => `Přesunout ${name}?`,
  "settings.reorderConsequence": "Tímto se změní pořadí řádků tabule všem.",
  "settings.reorderConfirm": "Změnit pořadí",

  "settings.thresholdTitle": "Hranice stagnace",
  "settings.thresholdDescription":
    "Jak dlouho může příležitost ležet v jedné fázi, než ji tabule označí jako stagnující.",
  "settings.thresholdLoading": "Načítání nastavení…",
  "settings.thresholdFailed": "Nastavení se nepodařilo načíst.",
  "settings.thresholdDays": "Dnů ve fázi",
  "settings.thresholdRange": (min: number, max: number) => `Zadejte celé číslo mezi ${min} a ${max}.`,
  "settings.thresholdWarn": (days: number) =>
    `Pod ${days} dní bude jako stagnující vypadat většina aktivních příležitostí.`,
  "settings.thresholdRetroPrefix": "Nastavení se použije",
  "settings.thresholdRetroEmphasis": "zpětně",
  "settings.thresholdRetroSuffix":
    ": každá otevřená příležitost se překlasifikuje okamžitě, na všech tabulích, ne jen ty, které začnou stagnovat od teď.",
  "settings.thresholdSave": "Uložit hranici",
  "settings.thresholdLastChanged": (when: string) => `Naposledy změněno ${when}`,
  "settings.thresholdLastChangedBy": (when: string, who: string) => `Naposledy změněno ${when} — ${who}`,

  // ─── analytics ───────────────────────────────────────────────────────────────
  "analytics.detailedView": "Souhrny",
  "analytics.tabPhase": "Podle fáze",
  "analytics.tabManager": "Podle manažera",
  "analytics.stalledAmount": (amount: string) => `${amount} stagnuje`,
  "analytics.noManagers": "V tomto zobrazení není žádná otevřená pipeline k rozpadu.",

  "strip.openCount": "Otevřené příležitosti",
  "strip.pipelineValue": "Hodnota pipeline",
  "strip.closing": (quarter: string) => `Uzavření ${quarter}`,
  "strip.closingThisQuarter": "Uzavírá se tento kvartál",
  "strip.stalled": "Stagnující",
  "strip.largest": "Největší příležitost",
  "strip.loadFailed": "Souhrn se nepodařilo načíst.",
  "strip.loading": "Načítání souhrnu…",

  // ─── feedback ────────────────────────────────────────────────────────────────
  "toast.genericFailure": "Nepovedlo se.",
  "toast.staleTitle": "Někdo to změnil dřív.",
  "toast.staleDescription": "Jeho změna je teď na obrazovce — zkontrolujte ji a proveďte svou znovu.",
  "toast.reference": (requestId: string) => `Reference ${requestId}`,
  "toast.created": (name: string) => `${name} — vytvořeno`,
  "toast.opportunityDeleted": "Příležitost smazána",
  "toast.savedName": "Název uložen",
  "toast.savedRevenue": "Očekávaný obrat uložen",
  "toast.savedCloseDate": "Očekávané datum uzavření uloženo",
  "toast.savedClient": "Klient uložen",
  "toast.savedNote": "Poznámka uložena",
  "toast.removedNote": "Poznámka odstraněna",
  "toast.savedOpportunity": "Uloženo",
  "toast.reassignedTo": (name: string) => `Přeřazeno na ${name}`,
  "toast.closedWon": "Uzavřeno jako vyhrané",
  "toast.closedLost": "Uzavřeno jako prohrané",
  "toast.reopened": "Znovu otevřeno",
  "toast.clientRenamed": "Klient přejmenován",
  "toast.clientReactivated": "Klient znovu aktivován",
  "toast.clientDeactivated": "Klient deaktivován",
  "toast.invitationSentTo": (email: string) => `Pozvánka odeslána na ${email}`,
  "toast.invitationEmailSent": "E-mail s pozvánkou odeslán",
  "toast.invitationLinkReady": "Odkaz s pozvánkou je připraven ke zkopírování",
  "toast.invitationRevoked": "Pozvánka zrušena",
  "toast.roleChanged": "Role změněna",
  "toast.userReactivated": "Uživatel znovu aktivován",
  "toast.userDeactivated": "Uživatel deaktivován",
  "toast.colourSaved": "Barva uložena",
  "toast.stageAdded": "Fáze přidána",
  "toast.stageRenamed": "Fáze přejmenována",
  "toast.stageTypeChanged": "Typ fáze změněn",
  "toast.stageUpdated": "Fáze upravena",
  "toast.orderChanged": "Pořadí změněno",
  "toast.settingSaved": "Nastavení uloženo",

  // ─── API failures, by code ───────────────────────────────────────────────────
  "error.UNAUTHENTICATED": "Vaše přihlášení vypršelo. Přihlaste se znovu.",
  "error.FORBIDDEN": "K této akci nemáte oprávnění.",
  "error.CONFLICT": "To je v rozporu s něčím, co už je zaznamenané.",
  "error.RATE_LIMITED": "Příliš mnoho pokusů. Chvíli počkejte a zkuste to znovu.",
  "error.UNAVAILABLE": "Server je nedostupný. Zkontrolujte připojení a zkuste to znovu.",
  "error.INTERNAL": "Na serveru se něco pokazilo. Zkuste to znovu.",

  // ─── signing in ──────────────────────────────────────────────────────────────
  "auth.signInLead": "Přihlaste se, prosím.",
  "auth.continueWithMicrosoft": "Pokračovat přes Microsoft",
  "auth.or": "nebo",
  "auth.email": "E-mail",
  "auth.password": "Heslo",
  "auth.signIn": "Přihlásit se",
  "auth.signingIn": "Přihlašování…",
  "auth.forgot": "Zapomněli jste heslo?",
  "auth.signInFailed": "Přihlášení se nepovedlo. Zkuste to znovu.",
  "auth.devTitle": "Vývojové přihlášení",
  "auth.devDescription": "Vstup jako nasazený uživatel bez hesla. Zapíná se přes AUTH_MODE=dev.",
  "auth.unprotectedTitle": "Nechráněný přístup je zapnutý",
  "auth.unprotectedDescription":
    "Toto nasazení umožňuje přihlášení komukoli bez přihlašovacích údajů. Vypněte ho, než v něm budou skutečná data.",
  "auth.noSeededUsersPrefix": "Žádní nasazení uživatelé — spusťte",
  "auth.noSeededUsersSuffix": ".",

  "setPassword.lead": "Zvolte si heslo ke svému účtu.",
  "setPassword.deadLead": "Tento odkaz nelze použít.",
  "setPassword.settingFor": "Nastavujete heslo pro",
  "setPassword.newPassword": "Nové heslo",
  "setPassword.confirm": "Potvrzení hesla",
  "setPassword.hint": (min: number) => `Alespoň ${min} znaků. Délka je důležitější než složitost.`,
  "setPassword.submit": "Nastavit heslo",
  "setPassword.tooShort": (min: number) => `Použijte alespoň ${min} znaků`,
  "setPassword.mismatch": "Hesla se neshodují",
  "setPassword.missingToken": "V tomto odkazu chybí token. Požádejte o nový.",
  "setPassword.failed": "Nepovedlo se. Zkuste to znovu.",
  "setPassword.requestNew": "Požádat o nový odkaz",
  "setPassword.backToSignIn": "Zpět na přihlášení",
  "setPassword.expired": "Tomuto odkazu vypršela platnost.",
  "setPassword.revoked": "Tento odkaz zrušil správce.",
  "setPassword.redeemed": "Tento odkaz už byl použit. Přihlaste se svým heslem, nebo požádejte o obnovení.",
  "setPassword.deactivated": "Tento účet byl deaktivován.",
  "setPassword.invalid": "Tento odkaz není platný.",

  "forgot.lead": "Zadejte svůj e-mail a pošleme vám odkaz pro obnovení hesla.",
  "forgot.sentLead": "Zkontrolujte si e-mail.",
  "forgot.sentPrefix": "Pokud má",
  "forgot.sentSuffix": "účet s heslem, odkaz pro obnovení je na cestě. Platí dvě hodiny.",
  "forgot.submit": "Poslat odkaz",
  "forgot.sending": "Odesílání…",

  // ─── document titles ─────────────────────────────────────────────────────────
  "title.document": (subject: string) => `${subject} · Sales Board`,
};
